import { type ReactNode, useState } from "react";
import type { Atom } from "@tanstack/react-store";
import {
    flexRender,
    type RowData,
    type SortDirection,
} from "@tanstack/react-table";
import { SortIcon } from "@icons";
import { TreeHeaderSpacer, TreeHeaderToggle } from "../cells/ExpandCell";
import { resolveColumnMeta } from "../columns/columnKinds";
import { isDisplayColumnKind } from "../columns/columnLayout";
import type {
    DataGridColumnInstance,
    DataGridHeader,
    DataGridPin,
} from "../columns/types";
import { useHeaderDragProps } from "../features/dnd/useHeaderDragProps";
import type { GridController } from "../focus/gridController";
import { getColumnStyle } from "../layout/columnVars";
import {
    getHeaderMenuEntries,
    HeaderMenu,
    hasHeaderMenu,
    SORT_ACTION_LABELS,
    useHeaderMenuVariant,
} from "./HeaderMenu";
import { ResizeHandle } from "./ResizeHandle";
import styles from "./Header.module.scss";

interface HeaderCellProps<TData extends RowData> {
    header: DataGridHeader<TData>;
    controller: GridController;
    pin: DataGridPin | false;
    pinEdge: DataGridPin | undefined;
    ariaColIndex: number;
    sortDirection: SortDirection | false;
    /**
     * What the next header click sets. Passed in with `sortDirection` because
     * `column` stays the same object while sorting changes.
     */
    nextSortOrder: SortDirection | false;
    /** Position among active sorts; shown only when several are active. */
    sortIndex: number;
    isMultiSort: boolean;
    columnVariants: Atom<Record<string, string>>;
}

const ARIA_SORT: Record<SortDirection, "ascending" | "descending"> = {
    asc: "ascending",
    desc: "descending",
};

/** What the next click does, shown by the table's shared tooltip. */
const getSortTooltip = (nextOrder: SortDirection | false) =>
    SORT_ACTION_LABELS[nextOrder || "clear"];

interface HeaderLabelProps<TData extends RowData> extends Pick<
    HeaderCellProps<TData>,
    "sortDirection" | "nextSortOrder" | "sortIndex" | "isMultiSort"
> {
    column: DataGridColumnInstance<TData>;
    label: ReactNode;
}

/** The header's focus target: a sort button, or the bare label. */
const HeaderLabel = <TData extends RowData>({
    column,
    label,
    sortDirection,
    nextSortOrder,
    sortIndex,
    isMultiSort,
}: Readonly<HeaderLabelProps<TData>>) => {
    if (!column.getCanSort()) {
        return (
            <span
                tabIndex={-1}
                className={styles.headerStatic}
                data-dt-header-focus=""
                data-column-id={column.id}
            >
                {label}
            </span>
        );
    }
    return (
        <button
            type="button"
            tabIndex={-1}
            className={styles.sortButton}
            data-dt-header-focus=""
            data-column-id={column.id}
            data-sorted={sortDirection || undefined}
            data-cell-tooltip={getSortTooltip(nextSortOrder)}
            data-cell-tooltip-always=""
            onClick={column.getToggleSortingHandler()}
        >
            <span className={styles.sortPill}>
                {label}
                {sortDirection ? (
                    <span className={styles.sortIndicator} aria-hidden>
                        <SortIcon />
                        {isMultiSort && sortIndex >= 0 ? (
                            <span className={styles.sortIndex}>
                                {sortIndex + 1}
                            </span>
                        ) : null}
                    </span>
                ) : null}
            </span>
        </button>
    );
};

/**
 * Leaf header: a tree column's expand all, sort button (click cycles
 * ascending, descending, off; Shift adds a secondary sort), label, sort arrow
 * and index, column menu, resize handle.
 */
export const HeaderCell = <TData extends RowData>({
    header,
    controller,
    pin,
    pinEdge,
    ariaColIndex,
    sortDirection,
    nextSortOrder,
    sortIndex,
    isMultiSort,
    columnVariants,
}: Readonly<HeaderCellProps<TData>>) => {
    const column = header.column;
    const meta = resolveColumnMeta(column.columnDef, column.id);
    const [isFocusWithin, setIsFocusWithin] = useState(false);
    const canSort = column.getCanSort();
    const menuEntries = getHeaderMenuEntries({ column, meta, sortDirection });
    const variant = useHeaderMenuVariant({
        columnId: column.id,
        variants: meta.variants,
        columnVariants,
    });
    const hasMenu = hasHeaderMenu(menuEntries, variant);
    // Display columns name themselves through `aria-label`, not visible text.
    const isDisplayHeader = isDisplayColumnKind(meta.kind);
    const customHeader = column.columnDef.header;
    let content: ReactNode = isDisplayHeader ? null : meta.headerLabel;
    if (typeof customHeader === "function") {
        content = flexRender(customHeader, header.getContext());
    }
    // A display header's control (select all) centers in the cell like the
    // row controls below it, and its hover halo stays unclipped.
    const label = isDisplayHeader ? (
        content
    ) : (
        <span className={styles.headerLabel}>{content}</span>
    );
    const dragProps = useHeaderDragProps({
        controller,
        columnId: column.id,
        isDisplayHeader,
    });
    let ariaSort: "ascending" | "descending" | "none" | undefined;
    if (canSort) {
        ariaSort = sortDirection ? ARIA_SORT[sortDirection] : "none";
    }

    return (
        <div
            role="columnheader"
            aria-colindex={ariaColIndex}
            aria-sort={ariaSort}
            aria-label={isDisplayHeader ? meta.ariaLabel : meta.headerLabel}
            className={styles.headerCell}
            style={getColumnStyle(column.id, pin)}
            data-column-id={column.id}
            data-align={meta.align}
            data-has-menu={hasMenu || undefined}
            data-pin={pin || undefined}
            data-pin-edge={pinEdge}
            data-tree={meta.tree || undefined}
            {...dragProps}
            onFocus={() => setIsFocusWithin(true)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    setIsFocusWithin(false);
                }
            }}
        >
            {meta.tree && meta.expandAll ? (
                <TreeHeaderToggle context={header.getContext()} />
            ) : null}
            {meta.tree && !meta.expandAll ? <TreeHeaderSpacer /> : null}
            <HeaderLabel
                column={column}
                label={label}
                sortDirection={sortDirection}
                nextSortOrder={nextSortOrder}
                sortIndex={sortIndex}
                isMultiSort={isMultiSort}
            />
            {hasMenu ? (
                <HeaderMenu
                    entries={menuEntries}
                    variant={variant}
                    label={meta.headerLabel}
                    isTabbable={isFocusWithin}
                />
            ) : null}
            {column.getCanResize() ? (
                <ResizeHandle
                    header={header}
                    controller={controller}
                    label={meta.headerLabel}
                    isTabbable={isFocusWithin}
                    edge={pin === "end" ? "start" : "end"}
                />
            ) : null}
        </div>
    );
};
